import type { ICommandMenuAction } from './ICommandMenuAction.js'
import * as chevronIconLeft from '@nordhealth/icons/lib/assets/arrow-left.js'
import * as chevronIconRight from '@nordhealth/icons/lib/assets/arrow-right.js'
import * as commandKeyIcon from '@nordhealth/icons/lib/assets/keyboard-command.js'
import * as altKeyIcon from '@nordhealth/icons/lib/assets/keyboard-option.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { classMap } from 'lit/directives/class-map.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { observe } from '../common/decorators/observe.js'
import Icon from '../icon/Icon.js'
import style from './CommandMenuAction.css'

import '../visually-hidden/VisuallyHidden.js'

const KEY_REGEX = /(?:Key|Digit)([A-Z0-9])/g
const isMacintosh = () => navigator.platform.includes('Mac')

Icon.registerIcon(chevronIconLeft)
Icon.registerIcon(chevronIconRight)
Icon.registerIcon(altKeyIcon)
Icon.registerIcon(commandKeyIcon)

/**
 * Command Menu Action displays a single action that can be executed by the user. For usage examples, please see Command Menu component.
 *
 * @status internal
 */
@customElement('nord-command-menu-action')
export default class CommandMenuAction extends LitElement {
  static styles = style

  private direction = new DirectionController(this)

  @property({ type: Object }) command!: ICommandMenuAction

  @property({ type: Boolean }) selected = false

  override render() {
    return html`
      <div
        class=${classMap({
          'n-selected': this.selected,
          'n-command': true,
        })}
      >
        <div aria-hidden="true" class="n-command-icon">
          <nord-icon size="s" name=${this.getIconName()}></nord-icon>
        </div>
        <div class="n-title">${this.command.title}</div>
        ${this.renderShortcut()}
      </div>
    `
  }

  /**
   * Scroll to show element
   */
  @observe('selected')
  protected ensureInView() {
    if (this.selected) {
      requestAnimationFrame(() => this.scrollIntoView({ block: 'nearest' }))
    }
  }

  private getIconName() {
    if (this.command.icon) {
      return this.command.icon
    }

    return this.direction.isLTR ? chevronIconRight.title : chevronIconLeft.title
  }

  private renderShortcut() {
    if (!this.command.shortcut) {
      return nothing
    }

    const shortcutTokens = this.command.shortcut.replace(KEY_REGEX, '$1').split(/[\s+]+/).filter(Boolean)
    const accessibleShortcut = shortcutTokens
      .map(shortcutToken => this.getAccessibleShortcutToken(shortcutToken))
      .join(' ')
    const shortcutChips
      = this.command.combineShortcutDisplay
        ? [
            html`<div class="n-shortcut">
              ${shortcutTokens.map(
                (shortcutToken, index) => html`${index ? ' ' : nothing}${this.renderShortcutToken(shortcutToken)}`,
              )}
            </div>`,
          ]
        : shortcutTokens.map(
            shortcutToken => html`<div class="n-shortcut">${this.renderShortcutToken(shortcutToken)}</div>`,
          )

    return html`
      <nord-visually-hidden>, ${accessibleShortcut}</nord-visually-hidden>

      <div aria-hidden="true" class="n-shortcuts">${shortcutChips}</div>
    `
  }

  private renderShortcutToken(shortcutToken: string) {
    const normalizedShortcutToken = shortcutToken.toLowerCase()

    if (normalizedShortcutToken === 'alt' && isMacintosh()) {
      return html`<nord-icon name=${altKeyIcon.title} size="s"></nord-icon>`
    }

    if (normalizedShortcutToken === '$mod') {
      return isMacintosh() ? html`<nord-icon name=${commandKeyIcon.title} size="s"></nord-icon>` : 'Ctrl'
    }

    return shortcutToken
  }

  private getAccessibleShortcutToken(shortcutToken: string) {
    const normalizedShortcutToken = shortcutToken.toLowerCase()

    if (normalizedShortcutToken === 'alt' && isMacintosh()) {
      return 'Option'
    }

    if (normalizedShortcutToken === '$mod') {
      return isMacintosh() ? 'Command' : 'Control'
    }

    return shortcutToken
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-command-menu-action': CommandMenuAction
  }
}
